import {
  DataType,
  FilterOperator,
  IndexType,
  SystemGeneratedTableColumnType,
} from '@doss/prisma/types'
import _ from 'lodash'
import { isValidMuiDateTimeString, toDateTimeString } from './dates.js'
import { getDataTypeForSystemGeneratedColumn } from './table-column.js'
import {
  OperandData,
  checkRangeFilter,
  checkValidColumnFilterOperand,
  dateTimeOperandSchema,
  validateStaticRangeFilter,
} from './types/table-column-filter-operand.js'

/**
 * Represents an argument used to filter a TableColumn in a Table.
 */
export interface FilterArg {
  columnId: string
  operator: FilterOperator
  operand?: Record<string, unknown> | null | undefined
  filterArray?: string[] | undefined | null
  filterString?: string | undefined | null
}

/**
 * Converts a TableColumnFilter operand to a filterString or filterArray
 * that is used for the filter input TableFilterInput, which is used to query for data based on that filter.
 *
 * Should not be used to convert dynamic range filters to filterString or filterArray.
 * Does not return anything if the operand is a dynamic range filter.
 */
export function getFilterStringAndArrayFromOperandData(
  operand: unknown,
  columnType: DataType,
): {
  filterString?: string
  filterArray?: string[]
} {
  const value =
    typeof operand === 'object' && operand !== null && 'data' in operand ? operand.data : operand
  const filterString = _.isNil(value) ? '' : value.toString()

  if (checkRangeFilter(columnType, operand)) {
    // Range filters only apply to NUMBER, DATE, and DATE_TIME columns
    if (validateStaticRangeFilter(columnType, operand)) {
      const { filterString: lower } = getFilterStringAndArrayFromOperandData(
        operand.data.lower,
        columnType,
      )
      const { filterString: upper } = getFilterStringAndArrayFromOperandData(
        operand.data.upper,
        columnType,
      )
      if (!lower || !upper) {
        throw new Error('Invalid static range filter')
      }
      return {
        filterArray: [lower, upper],
      }
    } else {
      // Dynamic range has no static values, thus return empty object
      return {}
    }
  } else if (dateTimeOperandSchema.isValidSync(operand)) {
    // If the data is a valid date-time string (YYYY-MM-DDTHH:mm), convert it to an ISO string
    if (isValidMuiDateTimeString(filterString)) {
      return { filterString: toDateTimeString(filterString, { setToMidnightUTC: false }) }
    }
    return { filterString: operand.data }
  } else if (Array.isArray(value)) {
    return {
      filterArray: value.map((item) => item.toString()),
    }
  } else {
    return { filterString }
  }
}

/**
 * Helper function to convert a TableColumnFilter to a filter argument that can be used when quering
 * Table data.
 */
export function columnFilterToFilterArg(
  operand: OperandData | null,
  operator: FilterOperator,
  columnId: string,
  dataType: DataType,
): FilterArg {
  if (!operand) {
    return {
      columnId,
      operator,
      operand,
      filterArray: operand,
      filterString: operand,
    }
  }

  const { filterString, filterArray } = getFilterStringAndArrayFromOperandData(operand, dataType)
  return {
    columnId,
    operator,
    filterString,
    filterArray,
    operand,
  }
}

export interface ColumnMetadata {
  id: string
  type: DataType
  resolvedSourceType: DataType
  indexType: IndexType
}

export interface ColumnFilter {
  operator: FilterOperator
  operand: unknown
  tableColumn?: ColumnMetadata | null
  systemGeneratedColumnType?: SystemGeneratedTableColumnType | null
}

/**
 * Validates if a filter is valid and creates the filter argument if it is. Returns null
 * if the filter is invalid. Caller of the function is responsible for throwing an error
 *  if the filter is invalid. (Frontend - we don't want to throw an error, so we return null,
 *  backend - we want to throw an error)
 */
export function validateFilterArg(filter: ColumnFilter): FilterArg | null {
  let dataType: DataType
  let indexType: IndexType
  let columnId: string
  if (filter.tableColumn) {
    // Use resolved source type so validation matches backend (resolveTableColumnSourceType) and
    // columnFilterToFilterArg operand handling.
    dataType = filter.tableColumn.resolvedSourceType
    indexType = filter.tableColumn.indexType
    columnId = filter.tableColumn.id
  } else if (filter.systemGeneratedColumnType) {
    const resolved = getDataTypeForSystemGeneratedColumn(filter.systemGeneratedColumnType)
    dataType = resolved.dataType
    indexType = resolved.indexType
    columnId = filter.systemGeneratedColumnType
  } else {
    return null // caller decides to throw or skip
  }
  const operand = filter.operand
  const isValid = checkValidColumnFilterOperand(dataType, null, indexType, filter.operator, operand)

  if (!isValid) {
    return null
  }

  return columnFilterToFilterArg(operand as OperandData, filter.operator, columnId, dataType)
}
